import React, { useMemo, useState } from 'react';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import CentralizedSpinner from 'components/CentralizedSpinner';
import Snackbar from 'components/DesignSystem/Snackbar';
import Segmented from 'components/DesignSystem/Segmented';
import FluxAutomaticGeneralTable from 'components/Flux/GeneralTable';
import SystemCards from 'components/Flux/Automatic/Classic/Mobile/SystemCards';
import FullscreenTable from 'components/Layout/MobileShell/FullscreenTable';
import useClassicTableData from 'hooks/Flux/Automatic/Classic/useClassicTableData';
import useClassicRevisionList from 'hooks/Flux/Automatic/Classic/useClassicRevisionList';
import useFluxAutomaticZipDownload from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';
import buildClassicCards from 'helper/flux/classicCards';
import { cssVar } from 'constants/theme';

import ControlPanelSheet from './ControlPanelSheet';

import './index.scss';

type ViewMode = 'graph' | 'table';

export default function ClassicMobile() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('graph');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const {
    modelData,
    isFetching,
    model,
    forecastDateLabel,
    acomphDateLabel,
  } = useClassicTableData();

  const { selectedRevision } = useClassicRevisionList();

  const hasCards = !!buildClassicCards(modelData?.sistema)?.length;

  const {
    DownloadZipButton,
    DownloadConfirmationDialog,
  } = useFluxAutomaticZipDownload({
    review: undefined,
    models: [model],
    unidade: '%mlt',
    pastSimulationNames: [],
    type: 'classic',
    spatialSubdivisionParam: {},
    forecastDate: forecastDateLabel,
    acomphDate: acomphDateLabel,
  });

  const chips = useMemo(() => {
    const result: string[] = [];
    if (acomphDateLabel) result.push(`ACOMPH: ${acomphDateLabel}`);
    if (forecastDateLabel) result.push(`Simulação: ${forecastDateLabel}`);
    if (selectedRevision) result.push(`Revisão: ${selectedRevision}`);
    return result;
  }, [acomphDateLabel, forecastDateLabel, selectedRevision]);

  // Extraído para reaproveitar o mesmo JSX no card normal e dentro do FullscreenTable,
  // sem duplicar o .map das tabelas por sistema.
  const renderTables = (data: NonNullable<typeof modelData>) => (
    <div className="flux-automatic-classic-mobile-tables">
      {Object.keys(data).map((system) => (
        // Cada sistema (sistema/subsistemas/reservatorios) ganha seu próprio card branco
        // arredondado — mesmo padrão visual do QueryCard — com o nome como header do card,
        // em vez do rótulo viver na célula de canto da tabela.
        <div className="flux-table-card" key={system}>
          <div className="flux-table-card-header">{system}</div>
          {/* FluxAutomaticGeneralTable renderiza uma <table> nua, sem wrapper — sem este
              container de scroll o overflow propaga e a PÁGINA rola de lado (§4b). */}
          <div className="table-responsive flux-table-card-body">
            <FluxAutomaticGeneralTable
              data={data[system]}
              lastColAsPercent
              tableLabel=""
              stickyRowLabel
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flux-automatic-classic-mobile">
      <DownloadConfirmationDialog />

      <QueryCard>
        <QueryCardHeader
          label="Consulta atual"
          title={model || 'Nenhum modelo selecionado'}
          action={model ? <DownloadZipButton /> : undefined}
        >
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger onClick={() => setSheetOpen(true)} />
      </QueryCard>

      {isFetching && <CentralizedSpinner />}

      {!isFetching && !modelData && (
        <Snackbar>
          Nenhum dado para os filtros selecionados.
        </Snackbar>
      )}

      {!isFetching && modelData && (
        <div className="flux-automatic-classic-mobile-card">
          <div className="flux-automatic-classic-mobile-card-header">
            {hasCards && (
              <Segmented<ViewMode>
                size="sm"
                options={[
                  { label: 'Gráfico', value: 'graph' },
                  { label: 'Tabela', value: 'table' },
                ]}
                value={viewMode}
                onChange={setViewMode}
                ariaLabel="Modo de visualização"
              />
            )}

            {(!hasCards || viewMode === 'table') && (
              <button
                type="button"
                aria-label="Tela cheia"
                className="flux-automatic-classic-mobile-card-action"
                onClick={() => setFullscreenOpen(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              </button>
            )}
          </div>

          {hasCards && viewMode === 'graph' && <SystemCards data={modelData} />}

          {(!hasCards || viewMode === 'table') && renderTables(modelData)}
        </div>
      )}

      {modelData && (
        <FullscreenTable open={fullscreenOpen} dismiss={() => setFullscreenOpen(false)}>
          {renderTables(modelData)}
        </FullscreenTable>
      )}

      <ControlPanelSheet open={sheetOpen} dismiss={() => setSheetOpen(false)} />
    </div>
  );
}
